import { JsonObject, JsonProperty } from "typescript-json-serializer";
import { ExtentConfiguration } from "./geometry-configuration";
import {PortalConfiguration} from "./portal-configuration";

@JsonObject()
export class PortalItemConfiguration {
    /**
     * Indicates the level of access to this item: `private`, `shared`, `org`, or `public`.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-PortalItem.html#access)
     */
    @JsonProperty()
    access: "private" | "shared" | "org" | "public";

    /**
     * Information on the source of the item and its copyright status.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-PortalItem.html#accessInformation)
     */
    @JsonProperty()
    declare accessInformation: string;

    /**
     * An authorization string used to access the portal item.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-PortalItem.html#apiKey)
     */
    @JsonProperty()
    declare apiKey?: string;

    /**
     * Contains an array of objects containing proxy information for premium platform services.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-PortalItem.html#applicationProxies)
     */
    // @JsonProperty()
    // declare readonly applicationProxies: PortalItemApplicationProxies[];

    /**
     * Average rating.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-PortalItem.html#avgRating)
     */
    @JsonProperty()
    declare avgRating: number;

    /**
     * An array of organization categories that are set on the item.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-PortalItem.html#categories)
     */
    @JsonProperty()
    declare categories: string[];

    /**
     * The classification information for the item.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-PortalItem.html#classification)
     */
    @JsonProperty()
    declare classification?: HashMap<any>;

    /**
     * The date the item was created.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-PortalItem.html#created)
     */
    @JsonProperty()
    declare created: Date;

    /**
     * The item's locale information (language and country).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-PortalItem.html#culture)
     */
    @JsonProperty()
    declare culture: string;

    /**
     * The detailed description of the item.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-PortalItem.html#description)
     */
    @JsonProperty()
    declare description: string;

    /**
     * The geographic extent, or bounding rectangle, of the item.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-PortalItem.html#extent)
     */
    @JsonProperty()
    declare extent: ExtentConfiguration;

    /**
     * An array of group categories set on the item.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-PortalItem.html#groupCategories)
     */
    @JsonProperty()
    declare groupCategories: string[];

    /**
     * The unique id for the item.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-PortalItem.html#id)
     */
    @JsonProperty()
    declare id: string;

    /**
     * Indicates whether a layer can be created from this item using [Layer.fromPortalItem()](esri-layers-Layer.html#fromPortalItem).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-PortalItem.html#isLayer)
     */
    @JsonProperty()
    declare readonly isLayer: boolean;

    /**
     * Indicates whether an item can be updated and deleted.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-PortalItem.html#itemControl)
     */
    @JsonProperty()
    declare readonly itemControl: "admin" | "update" | "null";

    /**
     * The URL to the item.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-PortalItem.html#itemUrl)
     */
    @JsonProperty()
    declare readonly itemUrl: string;

    /**
     * Information on license or restrictions related to the item.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-PortalItem.html#licenseInfo)
     */
    @JsonProperty()
    declare licenseInfo: string;

    /**
     * Indicates whether the item's resources have loaded from the portal.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-PortalItem.html#loaded)
     */
    @JsonProperty()
    declare readonly loaded: boolean;

    /**
     * The date the item was last modified.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-PortalItem.html#modified)
     */
    @JsonProperty()
    declare modified: Date;

    /**
     * The name of the item.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-PortalItem.html#name)
     */
    @JsonProperty()
    declare name: string;

    /**
     * Number of comments on the item.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-PortalItem.html#numComments)
     */
    @JsonProperty()
    declare numComments: number;

    /**
     * Number of ratings on the item.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-PortalItem.html#numRatings)
     */
    @JsonProperty()
    declare numRatings: number;

    /**
     * Number of views on the item.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-PortalItem.html#numViews)
     */
    @JsonProperty()
    declare numViews: number;

    /**
     * The username of the user who owns this item.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-PortalItem.html#owner)
     */
    @JsonProperty()
    declare owner: string;

    /**
     * The ID of the folder in which the owner has stored the item.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-PortalItem.html#ownerFolder)
     */
    @JsonProperty()
    declare ownerFolder: string;

    /**
     * The portal that contains the item.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-PortalItem.html#portal)
     */
    @JsonProperty()
    declare portal: PortalConfiguration;

    /**
     * An array of string URLs.
     *
     * @default null
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-PortalItem.html#screenshots)
     */
    @JsonProperty()
    declare screenshots: string[];

    /**
     * The size of the item (in bytes).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-PortalItem.html#size)
     */
    @JsonProperty()
    declare size: number;

    /**
     * A summary description of the item.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-PortalItem.html#snippet)
     */
    @JsonProperty()
    declare snippet: string;

    /**
     * The JSON used to create the property values when the `PortalItem` is loaded.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-PortalItem.html#sourceJSON)
     */
    @JsonProperty()
    declare sourceJSON: any;

    /**
     * User defined tags that describe the item.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-PortalItem.html#tags)
     */
    @JsonProperty()
    declare tags: string[];

    /**
     * The URL to the thumbnail used for the item.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-PortalItem.html#thumbnailUrl)
     */
    declare readonly thumbnailUrl: string;

    /**
     * The title for the item.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-PortalItem.html#title)
     */
    @JsonProperty()
    declare title: string;

    /**
     * The GIS content type of this item.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-PortalItem.html#type)
     */
    @JsonProperty()
    declare type: string;

    /**
     * Type keywords that describe the type of content of this item.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-PortalItem.html#typeKeywords)
     */
    @JsonProperty()
    declare typeKeywords: string[];

    /**
     * The service URL of this item.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-PortalItem.html#url)
     */
    @JsonProperty()
    declare url: string;

    constructor() {}
}
